import { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { PRODUCTS } from "@/lib/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function isValidIndianMobile(value) {
  let d = (value || "").replace(/\D/g, "");
  if (d.length === 12 && d.startsWith("91")) d = d.slice(2);
  else if (d.length === 11 && d.startsWith("0")) d = d.slice(1);
  return d.length === 10 && /[6-9]/.test(d[0]);
}

export default function QuoteDialog({ open, onOpenChange, defaultType }) {
  const emptyForm = {
    name: "",
    phone: "",
    email: "",
    insurance_type: defaultType || "car",
    city: "",
    notes: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (defaultType) setForm((f) => ({ ...f, insurance_type: defaultType }));
  }, [defaultType]);

  useEffect(() => {
    if (open) {
      setSuccess(false);
      setErrors({});
    }
  }, [open]);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  const validate = () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2)
      e.name = "Please enter your full name";
    if (!isValidIndianMobile(form.phone))
      e.phone = "Enter a valid 10-digit mobile number";
    if (form.email && !EMAIL_RE.test(form.email))
      e.email = "Enter a valid email address";
    if (!form.insurance_type) e.insurance_type = "Please select insurance type";
    return e;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const eMap = validate();
    setErrors(eMap);
    if (Object.keys(eMap).length > 0) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, {
        ...form,
        email: form.email || null,
        city: form.city || null,
        notes: form.notes || null,
      });
      setSuccess(true);
      toast.success("Quote request received! We will call you shortly.");
      setForm(emptyForm);
      setErrors({});
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let msg = "Could not submit. Please call +91 8827794458 directly.";
      if (Array.isArray(detail) && detail.length) msg = detail[0]?.msg || msg;
      else if (typeof detail === "string") msg = detail;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="quote-dialog"
        className="max-w-xl p-0 overflow-hidden rounded-2xl"
      >
        <div className="bg-[#1e3a8a] text-white p-6 md:p-8">
          <DialogHeader className="space-y-1">
            <DialogTitle className="font-display text-2xl md:text-3xl font-extrabold">
              Get your free quote
            </DialogTitle>
            <DialogDescription className="text-slate-200 font-hindi">
              मिनटों में पाएं अपना मुफ़्त बीमा कोटेशन
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="p-6 md:p-8">
          {success ? (
            <div data-testid="quote-success" className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-emerald-100 mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="font-display mt-4 text-2xl font-bold text-slate-900">
                Request received!
              </h3>
              <p className="mt-2 text-slate-600">
                Our advisor will call you within a few hours with the best plans.
              </p>
              <Button
                data-testid="quote-success-close"
                onClick={() => onOpenChange(false)}
                className="mt-6 bg-[#f5b942] hover:bg-[#d99b28] text-slate-900 font-bold rounded-full px-6 h-11"
              >
                Done
              </Button>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate data-testid="quote-form" className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name *" htmlFor="q-name" error={errors.name}>
                  <Input
                    id="q-name"
                    data-testid="quote-name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Your full name"
                    className="h-11"
                    aria-invalid={!!errors.name}
                    required
                  />
                </Field>
                <Field label="Phone number *" htmlFor="q-phone" error={errors.phone}>
                  <Input
                    id="q-phone"
                    data-testid="quote-phone"
                    name="phone"
                    type="tel"
                    inputMode="numeric"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="+91 98XXX XXXXX"
                    className="h-11"
                    aria-invalid={!!errors.phone}
                    required
                  />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Email" htmlFor="q-email" error={errors.email}>
                  <Input
                    id="q-email"
                    data-testid="quote-email"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@example.com"
                    className="h-11"
                    aria-invalid={!!errors.email}
                  />
                </Field>
                <Field label="City" htmlFor="q-city">
                  <Input
                    id="q-city"
                    data-testid="quote-city"
                    name="city"
                    value={form.city}
                    onChange={onChange}
                    placeholder="e.g. Indore"
                    className="h-11"
                  />
                </Field>
              </div>

              <Field label="Insurance type *" htmlFor="q-type">
                <Select
                  value={form.insurance_type}
                  onValueChange={(v) =>
                    setForm((f) => ({ ...f, insurance_type: v }))
                  }
                >
                  <SelectTrigger
                    data-testid="quote-type-trigger"
                    id="q-type"
                    className="h-11"
                  >
                    <SelectValue placeholder="Select insurance type" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRODUCTS.map((p) => (
                      <SelectItem
                        key={p.slug}
                        value={p.slug}
                        data-testid={`quote-type-${p.slug}`}
                      >
                        {p.title_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Notes" htmlFor="q-notes">
                <Textarea
                  id="q-notes"
                  data-testid="quote-notes"
                  name="notes"
                  value={form.notes}
                  onChange={onChange}
                  placeholder="Any details that help us — vehicle, age, family size, current policy..."
                  rows={3}
                />
              </Field>

              <div className="flex items-center gap-2 text-xs text-slate-500">
                <ShieldCheck className="w-3.5 h-3.5" />
                Your info is safe — we never share with third parties.
              </div>

              <Button
                type="submit"
                data-testid="quote-submit"
                disabled={loading}
                className="w-full bg-[#f5b942] hover:bg-[#d99b28] text-slate-900 font-bold rounded-full h-12 shadow-md"
              >
                {loading ? "Submitting..." : "Request Free Quote"}
              </Button>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, htmlFor, error, children }) {
  return (
    <div>
      <Label htmlFor={htmlFor} className="text-sm font-semibold text-slate-700">
        {label}
      </Label>
      <div className="mt-1.5">{children}</div>
      {error && (
        <p
          data-testid={`error-${htmlFor}`}
          className="mt-1 text-xs font-medium text-red-600"
        >
          {error}
        </p>
      )}
    </div>
  );
}
